import { useEffect, useMemo, useState } from "react";
import { FaBuildingColumns } from "react-icons/fa6";
import { apiFetch } from "../lib/api";
import { toast } from "react-toastify";

type BankDetails = {
  id: number;
  account_name?: string;
  account_number?: string;
  bank_name?: string;
  branch?: string;
  ifsc?: string;
  swift?: string;
  tag?: string;
  tag_color?: string;
  notes?: string;
};

const bookmarkColors = ["#0f766e", "#2563eb", "#7c3aed", "#dc2626", "#f59e0b"];
const defaultBookmarkColor = bookmarkColors[0];

const emptyForm = {
  id: null as number | null,
  accountName: "",
  accountNumber: "",
  bankName: "",
  branch: "",
  ifsc: "",
  swift: "",
  tag: "",
  tagColor: defaultBookmarkColor,
  notes: "",
};

export default function BankDetailsPage() {
  const [bankDetails, setBankDetails] = useState<BankDetails[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState<string | null>(null);
  const [bankSearch, setBankSearch] = useState<string>("");

  const loadBankDetails = () => {
    apiFetch("/api/bank-details/all")
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setBankDetails(Array.isArray(data) ? data : []);
          return;
        }
        throw new Error("all endpoint unavailable");
      })
      .catch(() =>
        apiFetch("/api/bank-details")
          .then((res) => res.json())
          .then((data) => setBankDetails(data ? [data] : []))
          .catch(() => setBankDetails([]))
      );
  };

  useEffect(() => {
    loadBankDetails();
  }, []);

  const filteredBankDetails = useMemo(() => {
    const term = bankSearch.trim().toLowerCase();
    if (!term) {
      return bankDetails;
    }

    return bankDetails.filter((details) => {
      const accountName = details.account_name?.toLowerCase() || "";
      const accountNumber = details.account_number?.toLowerCase() || "";
      const bankName = details.bank_name?.toLowerCase() || "";
      const branch = details.branch?.toLowerCase() || "";
      const ifsc = details.ifsc?.toLowerCase() || "";
      const swift = details.swift?.toLowerCase() || "";
      const tag = details.tag?.toLowerCase() || "";

      return (
        accountName.includes(term) ||
        accountNumber.includes(term) ||
        bankName.includes(term) ||
        branch.includes(term) ||
        ifsc.includes(term) ||
        swift.includes(term) ||
        tag.includes(term)
      );
    });
  }, [bankDetails, bankSearch]);

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus("Saving bank details...");

    try {
      const response = await apiFetch(
        form.id ? `/api/bank-details/${form.id}` : "/api/bank-details",
        {
          method: form.id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to save");
      }

      setForm(emptyForm);
      const message = form.id ? "Bank details updated." : "Bank details saved.";
      setStatus(message);
      toast.success(message);
      loadBankDetails();
    } catch (error: any) {
      setStatus(error?.message || "Failed to save bank details.");
      toast.error(error?.message || "Failed to save bank details.");
    }
  };

  const handleEdit = (details: BankDetails) => {
    setForm({
      id: details.id,
      accountName: details.account_name || "",
      accountNumber: details.account_number || "",
      bankName: details.bank_name || "",
      branch: details.branch || "",
      ifsc: details.ifsc || "",
      swift: details.swift || "",
      tag: details.tag || "",
      tagColor: details.tag_color || defaultBookmarkColor,
      notes: details.notes || "",
    });
    setStatus(null);
  };

  const handleDuplicate = (details: BankDetails) => {
    setForm({
      id: null,
      accountName: details.account_name || "",
      accountNumber: details.account_number || "",
      bankName: details.bank_name || "",
      branch: details.branch || "",
      ifsc: details.ifsc || "",
      swift: details.swift || "",
      tag: details.tag || "",
      tagColor: details.tag_color || defaultBookmarkColor,
      notes: details.notes || "",
    });
    setStatus("Bank details copied. Save to create a new entry.");
    toast.info("Bank details copied. Save to create a new entry.");
  };

  const handleReset = () => {
    setForm(emptyForm);
    setStatus(null);
  };

  return (
    <section className="grid gap-6">
      <div className="card p-6">
        <h2 className="font-display text-xl font-semibold text-ink">Bank Details</h2>
        <p className="text-sm text-slate-500">
          Store your bank account information that should appear on invoices.
        </p>
      </div>

      <div className="grid items-start gap-6 md:grid-cols-[1.15fr_1fr]">
        <form className="card grid gap-4 p-6" onSubmit={handleSubmit}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-600">
              {form.id ? "Edit Bank Details" : "Add Bank Details"}
            </h3>
            {form.id && (
              <button type="button" className="button-secondary" onClick={handleReset}>
                New Details
              </button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-slate-600">Account Name</label>
              <input
                className="input"
                value={form.accountName}
                onChange={(e) => handleChange("accountName", e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Account Number</label>
              <input
                className="input"
                value={form.accountNumber}
                onChange={(e) => handleChange("accountNumber", e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Bank Name</label>
              <input
                className="input"
                value={form.bankName}
                onChange={(e) => handleChange("bankName", e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Branch</label>
              <input
                className="input"
                value={form.branch}
                onChange={(e) => handleChange("branch", e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">IFSC</label>
              <input
                className="input"
                value={form.ifsc}
                onChange={(e) => handleChange("ifsc", e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">SWIFT</label>
              <input
                className="input"
                value={form.swift}
                onChange={(e) => handleChange("swift", e.target.value)}
                autoCapitalize="characters"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-slate-600">TAG</label>
              <div className="mt-1 grid gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-[1fr_auto] sm:items-center">
                <div className="flex items-center gap-3">
                  <FaBuildingColumns
                    className="h-5 w-5 flex-none"
                    style={{ color: form.tagColor }}
                    aria-hidden="true"
                  />
                  <input
                    className="input"
                    placeholder="Example: Primary, GST, USD..."
                    value={form.tag}
                    onChange={(e) => handleChange("tag", e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  {bookmarkColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`h-7 w-7 rounded-full border-2 transition ${
                        form.tagColor === color
                          ? "border-slate-800"
                          : "border-white shadow ring-1 ring-slate-200"
                      }`}
                      style={{ backgroundColor: color }}
                      aria-label={`Use bookmark color ${color}`}
                      onClick={() => handleChange("tagColor", color)}
                    />
                  ))}
                  <input
                    type="color"
                    className="h-8 w-10 cursor-pointer rounded border border-slate-200 bg-white p-1"
                    value={form.tagColor}
                    aria-label="Custom bookmark color"
                    onChange={(e) => handleChange("tagColor", e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-600">Notes</label>
            <textarea
              className="input min-h-[90px]"
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button className="button" type="submit">
              {form.id ? "Update Bank Details" : "Save Bank Details"}
            </button>
            {form.id && (
              <button type="button" className="button-secondary" onClick={handleReset}>
                Cancel Edit
              </button>
            )}
            {status && <span className="text-sm text-slate-500">{status}</span>}
          </div>
        </form>

        <div className="card p-0 max-h-[calc(100vh-220px)] overflow-hidden lg:self-start">
          <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 p-6 backdrop-blur">
            <h3 className="text-sm font-semibold text-slate-600">Saved Bank Details</h3>
            <div className="mt-3 grid gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Search bank details
              </label>
              <input
                className="input"
                placeholder="Search by bank/account/IFSC/SWIFT/tag..."
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
              />
              {bankDetails.length > 0 &&
                filteredBankDetails.length === 0 &&
                bankSearch.trim() !== "" && (
                  <p className="text-xs text-slate-500">
                    No bank details match "{bankSearch.trim()}".
                  </p>
                )}
            </div>
          </div>

          <div className="max-h-[calc(100vh-220px-140px)] overflow-auto p-6">
            {filteredBankDetails.length === 0 ? (
              <p className="text-sm text-slate-500">
                {bankDetails.length === 0
                  ? "No bank details saved yet."
                  : "No bank details match this search."}
              </p>
            ) : (
              <ul className="grid gap-3">
                {filteredBankDetails.map((details) => (
                  <li
                    key={details.id}
                    className="rounded-xl border border-slate-200 bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="grid gap-1 text-sm text-slate-600">
                        {details.tag && (
                          <p className="mb-1 inline-flex w-fit items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700">
                            <FaBuildingColumns
                              className="h-3.5 w-3.5"
                              style={{ color: details.tag_color || defaultBookmarkColor }}
                              aria-hidden="true"
                            />
                            {details.tag}
                          </p>
                        )}
                        {details.account_name && (
                          <p>
                            <span className="font-semibold">Account Name:</span> {details.account_name}
                          </p>
                        )}
                        {details.account_number && (
                          <p>
                            <span className="font-semibold">Account Number:</span> {details.account_number}
                          </p>
                        )}
                        {details.bank_name && (
                          <p>
                            <span className="font-semibold">Bank:</span> {details.bank_name}
                          </p>
                        )}
                        {details.branch && (
                          <p>
                            <span className="font-semibold">Branch:</span> {details.branch}
                          </p>
                        )}
                        {details.ifsc && (
                          <p>
                            <span className="font-semibold">IFSC:</span> {details.ifsc}
                          </p>
                        )}
                        {details.swift && (
                          <p>
                            <span className="font-semibold">SWIFT:</span> {details.swift}
                          </p>
                        )}
                        {details.notes && (
                          <p>
                            <span className="font-semibold">Notes:</span> {details.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          className="button-secondary"
                          onClick={() => handleEdit(details)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="button-secondary"
                          onClick={() => handleDuplicate(details)}
                        >
                          Duplicate
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
